import React, { useEffect, useState } from 'react';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Briefcase, MapPin, PaperPlaneTilt, BookOpen, Question } from '@phosphor-icons/react';

const SOURCE_LABEL = {
  jobbank: 'JOB BANK',
  alis: 'ALIS',
  '80000hrs': '80,000 HOURS',
  manual: 'DIRECT',
  remotive: 'REMOTIVE',
};

const CandidateJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [applied, setApplied] = useState({});
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [previewJob, setPreviewJob] = useState(null);
  const [profile, setProfile] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    api.get('/applications/mine').then((r) => {
      const m = {};
      r.data.forEach((a) => { m[a.job_id] = a.status; });
      setApplied(m);
    }).catch(() => {});
    api.get('/candidates/me').then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const url = source ? `/jobs?source=${source}` : '/jobs';
    setLoading(true);
    api.get(url).then((r) => setJobs(r.data)).finally(() => setLoading(false));
  }, [source]);

  // Simple match score: overlap of job.required_skills with profile.projects.skills + headline/location country match.
  const matchScore = (job) => {
    if (!profile) return 0;
    let score = 0;
    const jobSkills = new Set((job.required_skills || []).map((s) => s.toLowerCase()));
    const mine = new Set();
    (profile.projects || []).forEach((p) => (p.skills || []).forEach((s) => mine.add(s.toLowerCase())));
    if (profile.headline) {
      profile.headline.toLowerCase().split(/[^a-z0-9]+/).forEach((w) => w && mine.add(w));
    }
    jobSkills.forEach((s) => { if (mine.has(s)) score += 3; });
    if (profile.country && job.country && profile.country === job.country) score += 1;
    return score;
  };

  const sortedJobs = [...jobs].sort((a, b) => matchScore(b) - matchScore(a));

  const apply = async (job) => {
    if (submittingId) return;
    // If the job has custom questions, candidate must use the dialog (which collects answers)
    const hasQs = (job.custom_questions || []).length > 0;
    if (hasQs && previewJob?.id !== job.id) {
      setPreviewJob(job);
      toast.info('Please answer the job-specific questions to apply.');
      return;
    }
    // Validate required custom questions are answered
    const required = (job.custom_questions || []).filter((q) => q.required);
    for (const q of required) {
      if (!answers[q.id] || !answers[q.id].trim()) {
        toast.error('Please answer all required questions.');
        return;
      }
    }
    setSubmittingId(job.id);
    try {
      await api.post('/applications/apply', { job_id: job.id, answers });
      toast.success('Application submitted');
      setApplied((a) => ({ ...a, [job.id]: 'applied' }));
      setPreviewJob(null);
      setAnswers({});
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      if (msg && msg.toLowerCase().includes('already applied')) {
        toast.error("You've already applied to this role.");
        setApplied((a) => ({ ...a, [job.id]: 'applied' }));
        return;
      }
      if (msg && msg.includes('complete at least one assessment')) {
        toast.error('Take an assessment first — we\'ll pick questions for this job.');
        try {
          const { data: assessments } = await api.get(`/assessments?role_family_id=${job.role_family_id}`);
          const mcq = assessments.find((a) => a.type === 'mcq') || assessments[0];
          if (mcq) {
            window.location.assign(`/candidate/assessments/${mcq.id}?for_job=${job.id}`);
            return;
          }
        } catch { /* ignore */ }
      }
      toast.error(msg || 'Apply failed');
    } finally {
      setSubmittingId(null);
    }
  };

  const sources = ['jobbank', 'alis', '80000hrs', 'manual'];

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">OPEN ROLES</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Jobs ingested from trusted sources.</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Ranked by match to your profile. Click <span className="mono">DETAILS</span> to read the full description before you apply.
        </p>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="source-filter">
          <Button size="sm" variant={source === null ? 'default' : 'outline'} className="rounded-none" onClick={() => setSource(null)} data-testid="filter-all">All</Button>
          {sources.map((s) => (
            <Button key={s} size="sm" variant={source === s ? 'default' : 'outline'} className="rounded-none" onClick={() => setSource(s)} data-testid={`filter-${s}`}>
              {SOURCE_LABEL[s]}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="mono text-sm text-muted-foreground">Loading...</div>
        ) : sortedJobs.length === 0 ? (
          <div className="text-sm text-muted-foreground">No jobs for this filter.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 grid-skeleton">
            {sortedJobs.map((j) => {
              const appStatus = applied[j.id];
              const ms = matchScore(j);
              return (
                <div key={j.id} className="p-6 bg-white hover:bg-secondary/40 transition-colors flex flex-col" data-testid={`job-card-${j.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="label-eyebrow text-primary">{SOURCE_LABEL[j.source] || j.source}</span>
                    <div className="flex items-center gap-2">
                      {ms > 0 && (
                        <span className="label-eyebrow bg-primary/10 text-primary px-2 py-0.5" data-testid={`match-${j.id}`}>
                          MATCH ·{ms}
                        </span>
                      )}
                      <Briefcase size={18} weight="duotone" className="text-muted-foreground" />
                    </div>
                  </div>
                  <button
                    onClick={() => setPreviewJob(j)}
                    className="text-left"
                    data-testid={`job-open-${j.id}`}
                  >
                    <h3 className="font-display font-black text-xl tracking-tight mb-2 hover:text-primary">{j.title}</h3>
                  </button>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                    <MapPin size={14} /> {j.location || 'Flexible'} · {j.country}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">{j.description}</p>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {(j.required_skills || []).slice(0, 4).map((s) => (
                      <span key={s} className="label-eyebrow bg-secondary px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewJob(j)}
                      className="rounded-none flex-1"
                      data-testid={`job-details-${j.id}`}
                    >
                      <BookOpen size={14} className="mr-1" /> Details
                    </Button>
                    {appStatus ? (
                      <Button size="sm" disabled variant="outline" className="rounded-none flex-1" data-testid={`job-applied-${j.id}`}>
                        {appStatus.toUpperCase()}
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => apply(j)} disabled={submittingId === j.id} className="rounded-none flex-1" data-testid={`job-apply-${j.id}`}>
                        {submittingId === j.id ? (
                          <>Submitting…</>
                        ) : (
                          <><PaperPlaneTilt size={14} className="mr-1" /> Apply</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Job detail dialog */}
        <Dialog open={!!previewJob} onOpenChange={(o) => !o && setPreviewJob(null)}>
          <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="job-preview-dialog">
            {previewJob && (
              <>
                <DialogHeader>
                  <div className="label-eyebrow text-primary">
                    {SOURCE_LABEL[previewJob.source] || previewJob.source}
                  </div>
                  <DialogTitle className="font-display font-black text-2xl tracking-tighter">
                    {previewJob.title}
                  </DialogTitle>
                  <DialogDescription className="flex items-center gap-1">
                    <MapPin size={12} /> {previewJob.location || 'Flexible'} · {previewJob.country}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <div className="label-eyebrow text-muted-foreground mb-2">REQUIRED SKILLS</div>
                    <div className="flex flex-wrap gap-1">
                      {(previewJob.required_skills || []).map((s) => (
                        <span key={s} className="label-eyebrow bg-secondary px-2 py-0.5">{s}</span>
                      ))}
                      {(!previewJob.required_skills || previewJob.required_skills.length === 0) && (
                        <span className="mono text-xs text-muted-foreground">Not listed</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="label-eyebrow text-muted-foreground mb-2">DESCRIPTION</div>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{previewJob.description}</p>
                  </div>
                  {matchScore(previewJob) > 0 && (
                    <div className="bg-primary/5 border border-primary/20 p-3 text-sm" data-testid="match-explainer">
                      <span className="label-eyebrow text-primary">MATCH · {matchScore(previewJob)}</span>{' '}
                      Based on overlap between your profile skills &amp; this role.
                    </div>
                  )}

                  {/* Job-specific custom questions */}
                  {(previewJob.custom_questions || []).length > 0 && !applied[previewJob.id] && (
                    <div className="border border-border p-4 bg-secondary/30" data-testid="apply-questions">
                      <div className="flex items-center gap-2 mb-3">
                        <Question size={16} weight="duotone" className="text-primary" />
                        <div className="label-eyebrow">JOB-SPECIFIC QUESTIONS · {previewJob.custom_questions.length}</div>
                      </div>
                      <ul className="space-y-3">
                        {previewJob.custom_questions.map((q, idx) => (
                          <li key={q.id}>
                            <label className="text-sm font-medium block mb-1">
                              {q.prompt}
                              {q.required && <span className="text-destructive ml-1">*</span>}
                            </label>
                            {q.type === 'long' ? (
                              <Textarea
                                rows={3}
                                className="rounded-none"
                                value={answers[q.id] || ''}
                                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                                data-testid={`apply-answer-${idx}`}
                              />
                            ) : (
                              <Input
                                className="rounded-none h-10"
                                value={answers[q.id] || ''}
                                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                                data-testid={`apply-answer-${idx}`}
                              />
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                  <Button variant="outline" className="rounded-none" onClick={() => setPreviewJob(null)} data-testid="job-preview-close">
                    Close
                  </Button>
                  {applied[previewJob.id] ? (
                    <Button disabled className="rounded-none" data-testid="job-preview-applied">
                      {(applied[previewJob.id] || 'applied').toUpperCase()}
                    </Button>
                  ) : (
                    <Button
                      className="rounded-none"
                      onClick={() => apply(previewJob)}
                      disabled={submittingId === previewJob.id}
                      data-testid="job-preview-apply"
                    >
                      {submittingId === previewJob.id ? (
                        <>Submitting…</>
                      ) : (
                        <><PaperPlaneTilt size={14} className="mr-1" /> Apply with proof</>
                      )}
                    </Button>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Shell>
  );
};

export default CandidateJobs;
